import { useDispatch, useSelector } from 'react-redux';
import {
	zoomAroundMousePosition,
	setZoomPreviewBox,
	centerCanvasAroundScenePosition,
	zoomSetAroundCanvasCenter,
	setCenterFramesForChatId,
} from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';
import { selectTransform, ZoomType } from 'redux/mode/reducers';
import { useState, useEffect } from 'react';
import { centerCanvasAroundSceneBoundingBox } from 'redux/mode/actions';
import { Rect, Point } from 'dragonmouse-shared/geometry';
import { getScenePoint, getSceneRect } from 'redux/mode/util';

const ZoomTool = () => {
	const dispatch = useDispatch();

	const [canvasClick, setCanvasClick] = useState<Point | null>(null);
	const DRAG_DELTA = 5;

	const { toolModified, canvasMousePosition, zoomPreviewBox, scale } = useSelector(
		(r: RootReducer) => ({
			toolModified: r.mode.toolModified,
			canvasMousePosition: r.mode.canvasMousePosition,
			zoomPreviewBox: r.mode.zoomPreviewBox,
			scale: selectTransform(r.mode).scale,
		})
	);

	useEffect(() => {
		return () => {
			dispatch(setZoomPreviewBox(null));
		};
	}, [dispatch]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onMouseDown = () => {
		setCanvasClick(canvasMousePosition);
	};

	const onMouseMove = () => {
		if (canvasClick) {
			if (zoomPreviewBox) {
				updateZoomPreviewBox(canvasClick, canvasMousePosition);
			} else {
				const deltaX = Math.abs(canvasMousePosition.x - canvasClick.x);
				const deltaY = Math.abs(canvasMousePosition.y - canvasClick.y);
				if (deltaX > DRAG_DELTA || deltaY > DRAG_DELTA) {
					updateZoomPreviewBox(canvasClick, canvasMousePosition);
				}
			}
		}
	};

	const onMouseUp = () => {
		// if zoomPreviewBox is set, we're dragging
		if (zoomPreviewBox) {
			// Only zoom if the box has some size
			if (zoomPreviewBox.width > 10 && zoomPreviewBox.height > 10) {
				if (toolModified) {
					// if the modifier key is pressed, zoom out a bit
					const sceneCenter = getSceneCenter(zoomPreviewBox);
					dispatch(centerCanvasAroundScenePosition({ position: sceneCenter, animated: true }));
					dispatch(zoomSetAroundCanvasCenter(scale * 0.25));
				} else {
					dispatch(
						centerCanvasAroundSceneBoundingBox({
							boundingBox: getSceneRect(zoomPreviewBox),
							animated: true,
							tightFit: true,
						})
					);
				}
			}
			dispatch(setZoomPreviewBox(null));
		} else {
			dispatch(zoomAroundMousePosition(toolModified ? ZoomType.ZOOM_OUT : ZoomType.ZOOM_IN));
		}
		dispatch(setCenterFramesForChatId(null));
		setCanvasClick(null);
	};

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------
	const updateZoomPreviewBox = (start: Point, end: Point) => {
		const rect: Rect = {
			x: Math.min(start.x, end.x),
			y: Math.min(start.y, end.y),
			width: Math.abs(start.x - end.x),
			height: Math.abs(start.y - end.y),
		};
		dispatch(setZoomPreviewBox(rect));
	};

	const getSceneCenter = (canvas: Rect) => {
		return getScenePoint({
			x: canvas.x + canvas.width / 2,
			y: canvas.y + canvas.height / 2,
		});
	};
	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	return (
		<S.Base
			onMouseDown={onMouseDown}
			onMouseMove={onMouseMove}
			onMouseUp={onMouseUp}
			cursor={toolModified ? 'zoom-out' : 'zoom-in'}
		></S.Base>
	);
};

export default ZoomTool;
